/**
 * Ternoa Shield — Contract & Network Configuration
 */

// ============================================================
// NETWORK
// ============================================================

export const TERNOA_CHAIN = {
  chainId: 752025,
  chainIdHex: '0xb7999',
  name: 'Ternoa zkEVM Mainnet',
  rpcUrl: 'https://rpc-mainnet.zkevm.ternoa.network',
  explorerUrl: 'https://explorer-mainnet.zkevm.ternoa.network',
  nativeCurrency: {
    name: 'CAPS',
    symbol: 'CAPS',
    decimals: 18,
  },
  aggLayerNetworkId: 13,
} as const;

// ============================================================
// TOKEN CONTRACTS (on Ternoa chain)
// ============================================================

export const TOKENS = {
  vbUSDC: {
    address: '0xac8dDbc1f261eE4632518a1A62c23cD2b5a52BAd' as `0x${string}`,
    symbol: 'vbUSDC',
    name: 'Bridged USDC',
    decimals: 6,
    icon: 'usdc',
  },
  vbUSDT: {
    address: '0x5453e111aCE935Ba404b9474E14fe7a883B97519' as `0x${string}`,
    symbol: 'vbUSDT',
    name: 'Bridged USDT',
    decimals: 6,
    icon: 'usdt',
  },
  vbETH: {
    address: '0xABb9adE1Bd2A3bDcCD4c91FB149685936aCb758E' as `0x${string}`,
    symbol: 'vbETH',
    name: 'Bridged ETH',
    decimals: 18,
    icon: 'eth',
  },
  CAPS: {
    address: null, // native gas token — use eth_getBalance
    symbol: 'CAPS',
    name: 'CAPS',
    decimals: 18,
    icon: 'caps',
  },
  KAT: {
    address: '0x1cA0C0c6FDeC12ed9d96aEaa736Dc363Ae29c4F0' as `0x${string}`,
    symbol: 'KAT',
    name: 'KAT Token', // on-chain name is "TAK Token" — displayed as KAT
    decimals: 18,
    icon: 'kat',
  },
} as const;

// ============================================================
// MERKLE DISTRIBUTOR (to be filled after deploy)
// ============================================================

export const MERKLE_DISTRIBUTOR = {
  address: '0x95a393D837A27C13064Bf19b22f32a94aB321D87' as `0x${string}`, // TODO: fill after contract deployment
} as const;

// ============================================================
// CAPS ON ETHEREUM (for reference / bridge)
// ============================================================

export const CAPS_ETHEREUM = {
  address: '0x03Be5C903c727Ee2C8C4e9bc0AcC860Cca4715e2' as `0x${string}`,
  chainId: 1,
} as const;

// ============================================================
// BRIDGE & EXTERNAL LINKS
// ============================================================

export const LINKS = {
  bridgeUSDC:
    'https://ui.agglayer.dev/?fromChain=1&toChain=752025&fromToken=0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48&toToken=0xac8dDbc1f261eE4632518a1A62c23cD2b5a52BAd',
  bridgeUSDT:
    'https://ui.agglayer.dev/?fromChain=1&toChain=752025&fromToken=0xdAC17F958D2ee523a2206206994597C13D831ec7&toToken=0x5453e111aCE935Ba404b9474E14fe7a883B97519',
  bridgeETH:
    'https://ui.agglayer.dev/?fromChain=1&toChain=752025&fromToken=0x0000000000000000000000000000000000000000&toToken=0xABb9adE1Bd2A3bDcCD4c91FB149685936aCb758E',
  bridgeCAPS:
    'https://ui.agglayer.dev/?fromChain=1&toChain=752025&fromToken=0x03be5c903c727ee2c8c4e9bc0acc860cca4715e2&toToken=0x0000000000000000000000000000000000000000',
  buyCAPS:
    'https://app.uniswap.org/explore/tokens/ethereum/0x03Be5C903c727Ee2C8C4e9bc0AcC860Cca4715e2?inputCurrency=NATIVE',
  explorer: 'https://explorer-mainnet.zkevm.ternoa.network',
  explorerTx: (hash: string) =>
    `https://explorer-mainnet.zkevm.ternoa.network/tx/${hash}`,
  explorerAddress: (addr: string) =>
    `https://explorer-mainnet.zkevm.ternoa.network/address/${addr}`,
} as const;

// ============================================================
// LEGACY STAKING CONTRACT
// ============================================================

export const STAKING_CONTRACT = {
  address: '0xdcCD76fC04a1ff72909696CfCd3136506576832e' as `0x${string}`,
} as const;

export const STAKING_ABI = [
  'function stake() payable',
  'function unstake(uint256 _amountToUnstake)',
  'function getStakeInfo(address _account) view returns (uint256 stakeAmount, bool hasStaked, uint256 lastStakeTimestamp)',
  'function getStake(address _account) view returns (uint256)',
  'function maxStakeLimit() view returns (uint256)',
  'function MIN_STAKE_LIMIT() view returns (uint256)',
  'event StakeAdded(address indexed _staker, uint256 _amountAdded, uint256 _newBalance)',
  'event StakeRemoved(address indexed _staker, uint256 _amountRemoved, uint256 _newBalance)',
] as const;

// ============================================================
// MINIMAL ERC-20 ABI (balanceOf + decimals + symbol)
// ============================================================

export const ERC20_ABI = [
  'function balanceOf(address owner) view returns (uint256)',
  'function decimals() view returns (uint8)',
  'function symbol() view returns (string)',
  'function name() view returns (string)',
  'function totalSupply() view returns (uint256)',
] as const;

// ============================================================
// MERKLE DISTRIBUTOR ABI (claim function)
// ============================================================

export const MERKLE_DISTRIBUTOR_ABI = [
  'function claim(uint256 epoch, uint256 amount, bytes32[] calldata merkleProof) external',
  'function hasClaimed(uint256 epoch, address account) view returns (bool)',
  'function epochs(uint256 epoch) view returns (bytes32 merkleRoot, uint256 totalAmount, uint256 claimedCount, uint256 startTime, bool active)',
  'function currentEpoch() view returns (uint256)',
  'event Claimed(address indexed user, uint256 indexed epoch, uint256 amount)',
] as const;
